<template>
  <div>
    <b-card
      no-body
      class="mb-0"
    >
      <div class="mx-2 mb-2 mt-1">
        <b-row>
          <!-- Search -->
          <b-col
            class="mt-1 mt-lg-0 d-flex align-items-end justify-content-end"
            cols="12"
            md="12"
          >
            <div class="d-flex align-items-center justify-content-end w-100">
              <b-button
                class="text-nowrap"
                variant="outline-primary"
                :to="{name: 'need-validate-physician-history-list'}"
              >
                Histórico <feather-icon icon="ExternalLinkIcon" />
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>
      <b-table
        ref="refPhysicianListTable"
        class="position-relative"
        :items="fetchPhysicians"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="Nenhum registro correspondente encontrado"
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- Column: Institution -->
        <template #cell(institution)="data">
          <b-link
            :to="{ name: 'institution-view', params: { id: data.item.institution_id } }"
            class="font-weight-bold d-block text-nowrap"
          >
            <div :title="data.item.institution_name">
              {{ truncateString(data.item.institution_name, 15) }}
            </div>
          </b-link>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              @click="openModalApprove(data.item)"
            >
              <feather-icon icon="CheckCircleIcon" />
              <span class="align-middle ml-50">Aprovar</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-card>

    <b-modal
      id="modal-approve-physician-modal"
      ref="approvePhysicianModal"
      title="Aprovar Profissional"
      hide-footer
      size="md"
      centered
    >
      <div>
        <div>
          <div class="mb-1">
            <h6>Nome atualizado:</h6>
            {{ approveData.name }}
            <span>
              <feather-icon
                icon="ArrowRightCircleIcon"
                class="update-icon"
                size="20"
              />
            </span>
            <span v-html="approveData.new_name" />
          </div>
          <label for="textarea-default">
            <b-form-checkbox
              v-model="approveData.change_name"
              name="change_name"
              class="custom-control-danger"
              switch
              inline
            >
              Atualizar nome?
            </b-form-checkbox>
          </label>
          <b-form-group
            class="mt-1"
            label="Motivo"
            label-for="motive"
          >
            <b-form-textarea
              id="motive"
              v-model="approveData.justification"
              name="motive"
              placeholder="Descreva o motivo..."
              rows="3"
            />
          </b-form-group>
        </div>
        <b-form-group
          class="mt-1"
          label="Arquivo"
          label-for="file"
        >
          <b-form-file
            ref="fileInput"
            v-model="file"
            placeholder="Escolha um arquivo (máx 15MB)..."
            drop-placeholder="Solte o arquivo aqui..."
            browse-text="Escolher arquivo"
            accept=".txt,.pdf,.doc,.docx,.odt,.jpg,.jpeg,.png"
            @input="checkFileSize"
          />
          <small
            v-if="fileError"
            class="text-danger"
          >{{ fileError }}</small>
        </b-form-group>
        <div class="text-right">
          <b-button
            variant="primary"
            @click="approve()"
          >
            Aprovar
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  BCard,
  BFormFile,
  BFormCheckbox,
  BRow,
  BCol,
  BTable,
  BLink,
  BButton,
  BDropdownItem,
  BDropdown,
  BModal,
  BFormTextarea,
  BFormGroup,
} from 'bootstrap-vue'
import store from '@/store'
import diff from 'diff'
import { onUnmounted, ref } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
import { maskCnpj, truncateString } from '@core/utils/utils'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import formError from '@core/comp-functions/forms/form-error'
import useNeedValidatePhysicianList from './useNeedValidatePhysicianList'
import physicianStoreModule from '../reportStoreModule'

export default {
  components: {
    BFormGroup,
    BFormTextarea,
    BFormCheckbox,
    BModal,
    BDropdown,
    BDropdownItem,
    BCard,
    BRow,
    BCol,
    BTable,
    BLink,
    BButton,
    BFormFile,
  },
  directives: {
    Ripple,
  },
  methods: {
    truncateString,
  },
  setup() {
    const PHYSICIAN_APP_STORE_MODULE_NAME = 'app-physician'

    // Register module
    if (!store.hasModule(PHYSICIAN_APP_STORE_MODULE_NAME)) store.registerModule(PHYSICIAN_APP_STORE_MODULE_NAME, physicianStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(PHYSICIAN_APP_STORE_MODULE_NAME)) store.unregisterModule(PHYSICIAN_APP_STORE_MODULE_NAME)
    })

    const approvePhysicianModal = ref(null)
    const fileInput = ref(null)
    const isValid = ref(true)
    const institutionOptions = ref(null)
    const file = ref(null)
    const fileError = ref(null)
    const approveData = ref({
      justification: '',
      name: '',
      changes: {},
      physician_id: null,
      change_name: false,
    })

    function highlightDiff(oldText, newText) {
      const changes = diff.diffWords(oldText, newText)
      return changes
        .map(part => {
          if (part.added) return `<b>${part.value}</b>`
          if (part.removed) return ''
          return part.value
        })
        .join('')
    }

    const checkFileSize = f => {
      if (!f) return

      fileError.value = null
      const maxSizeMB = 15
      const maxSizeBytes = maxSizeMB * 1024 * 1024
      if (f.size > maxSizeBytes) {
        fileError.value = `Arquivo muito grande. Tamanho máximo: ${maxSizeMB} MB.`
        file.value = null
        fileInput.value.reset()
      }
    }

    const openModalApprove = item => {
      const changes = item.validate_cpf || {}
      file.value = null
      approveData.value.name = item.name
      approveData.value.justification = ''
      approveData.value.physician_id = item.id
      approveData.value.new_name = highlightDiff(item.name, changes.name || '')
      approvePhysicianModal.value.show()
    }

    const fetchInstitutions = () => {
      store.dispatch('app-physician/fetchInstitutions', { perPage: 99999999 })
        .then(response => {
          institutionOptions.value = response.data.data.map(inst => ({
            value: inst.id,
            label: `${truncateString(inst.name, 18)} | ${maskCnpj(inst.cnpj)}`,
          }))
        })
    }
    fetchInstitutions()

    const {
      fetchPhysicians,
      tableColumns,
      perPage,
      currentPage,
      totalPhysicians,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPhysicianListTable,
      refetchData,
      scheduleRestrictionFilter,
      beginDate,
      endDate,
      institutionId,
    } = useNeedValidatePhysicianList()

    const approve = () => {
      const data = {
        ...approveData.value,
        file: file.value,
      }
      store.dispatch('app-physician/approvePhysician', data)
        .then(() => {
          refetchData()
          approvePhysicianModal.value.hide()
        }).catch(error => {
          const { getErrorMessage } = formError(null, error)
          Vue.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Erro',
              icon: 'XOctagonIcon',
              variant: 'danger',
              text: getErrorMessage(),
            },
          })
        })
    }

    return {
      isValid,
      approvePhysicianModal,
      fetchPhysicians,
      approveData,
      tableColumns,
      perPage,
      currentPage,
      totalPhysicians,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPhysicianListTable,
      refetchData,
      beginDate,
      endDate,
      approve,
      openModalApprove,
      file,

      // Filter
      avatarText,

      // Extra Filters
      scheduleRestrictionFilter,
      institutionId,
      institutionOptions,

      checkFileSize,
      fileError,
      fileInput,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.physician-class {
  width: 15px;
  height: 15px;
  border-spacing: 0!important;
  margin-top: 2px;
}
.physician-class {
  width: 15px;
  height: 15px;
  border-spacing: 0!important;
  margin-top: 2px;
  &:before {
    content: '';
    transform: translateY(3px);
    width: 15px;
    height: 15px;
    display: inline-block;
    border-radius: 15px;
    background-position: center center;
    background-repeat: no-repeat;
  }
  &-red {
    &:before {
      background-color: #f08182;
    }
  }
  &-yellow {
    &:before {
      background-color: #ffb976;
    }
  }
  &-green {
    &:before {
      background-color: #48da89;
    }
  }
}
@media (max-width: 1400px) {
  .v-select {
    font-size: 12px;
  }
}
.v-select {
  .physician-class {
    &:before {
      transform: translateY(2px) translateX(-4px);
    }
  }
}
.vs__selected {
  .physician-class {
    &:before {
      transform: translateY(-4px) translateX(-2px);
    }
  }
}
.update-icon {
  margin: 0 5px;
  color: #f08182;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
